import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getMyVendor, updateMyVendor, deleteMyVendor } from "@/lib/vendor.functions";
import { getMyDashboard } from "@/lib/profile.functions";
import { getMyStripeStatus, startStripeOnboarding, disableStripePayments } from "@/lib/stripe-connect.functions";

import { ImageUpload } from "@/components/ImageUpload";

export const Route = createFileRoute("/_authenticated/trgovina")({
  head: () => ({ meta: [{ title: "Urejanje trgovine — kupish.si" }] }),
  component: Page,
});

type Form = {
  store_name: string;
  description: string;
  city: string;
  country: string;
  address_1: string;
  postcode: string;
  account_holder: string;
  iban: string;
  bank_name: string;
  image_url: string | null;
  banner_url: string | null;
  instagram: string;
  youtube: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  shipping_policy: string;
  refund_policy: string;
  message_to_buyers: string;
  shipping_fee: string;
  free_shipping_over: string;
  public_phone: string;
  phone_public: boolean;
};

const empty: Form = {
  store_name: "", description: "", city: "", country: "Slovenija", address_1: "", postcode: "",
  account_holder: "", iban: "", bank_name: "",
  image_url: null, banner_url: null, instagram: "", youtube: "", linkedin: "", facebook: "", tiktok: "",
  shipping_policy: "", refund_policy: "", message_to_buyers: "",
  shipping_fee: "0", free_shipping_over: "",
  public_phone: "", phone_public: false,
};

const SI_IBAN_RE = /^SI56\d{15}$/;
function normalizeIban(v: string) { return v.replace(/\s+/g, "").toUpperCase(); }
function formatIban(v: string) {
  const n = normalizeIban(v);
  return n.replace(/(.{4})/g, "$1 ").trim();
}

function Page() {
  const fetchVendor = useServerFn(getMyVendor);
  const fetchDash = useServerFn(getMyDashboard);
  const save = useServerFn(updateMyVendor);
  const remove = useServerFn(deleteMyVendor);
  const navigate = useNavigate();
  const { data, isLoading, refetch, isFetching } = useQuery({ queryKey: ["my-vendor"], queryFn: () => fetchVendor() });
  const { data: dash } = useQuery({ queryKey: ["dashboard-sub"], queryFn: () => fetchDash() });
  const isActiveMember = dash?.subscription?.status === "active" || dash?.subscription?.status === "trialing" || (dash?.roles ?? []).includes("admin");
  const [form, setForm] = useState<Form>(empty);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);


  useEffect(() => {
    if (data) {
      setForm({
        store_name: data.store_name ?? "",
        description: data.description ?? "",
        city: data.city ?? "",
        country: data.country ?? "Slovenija",
        address_1: data.address_1 ?? "",
        postcode: data.postcode ?? "",
        account_holder: (data as { account_holder?: string | null }).account_holder ?? "",
        iban: (data as { iban?: string | null }).iban ? formatIban((data as { iban: string }).iban) : "",
        bank_name: (data as { bank_name?: string | null }).bank_name ?? "",
        image_url: data.image_url ?? null,
        banner_url: data.banner_url ?? null,
        instagram: data.instagram ?? "",
        youtube: data.youtube ?? "",
        linkedin: data.linkedin ?? "",
        facebook: data.facebook ?? "",
        tiktok: data.tiktok ?? "",
        shipping_policy: data.shipping_policy ?? "",
        refund_policy: data.refund_policy ?? "",
        message_to_buyers: data.message_to_buyers ?? "",
        shipping_fee: data.shipping_fee != null ? String(data.shipping_fee) : "0",
        free_shipping_over: data.free_shipping_over != null ? String(data.free_shipping_over) : "",
        public_phone: (data as { public_phone?: string | null }).public_phone ?? "",
        phone_public: Boolean((data as { phone_public?: boolean | null }).phone_public),
      });
    }
  }, [data]);

  const upd = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const ibanNorm = normalizeIban(form.iban);
  const ibanValid = SI_IBAN_RE.test(ibanNorm);
  const phoneValid = form.public_phone.trim().length >= 6;
  const payoutComplete = Boolean(
    form.address_1.trim() && form.city.trim() && form.postcode.trim() &&
    form.account_holder.trim() && ibanValid && phoneValid,
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (!payoutComplete) {
      setMsg({ type: "err", text: "Naslov, mesto, pošta, imetnik računa, TRR (SI56…) in telefonska številka so obvezni." });
      return;
    }
    setSaving(true);
    try {
      await save({
        data: {
          store_name: form.store_name,
          description: form.description || null,
          city: form.city || null,
          country: form.country || null,
          address_1: form.address_1 || null,
          postcode: form.postcode || null,
          account_holder: form.account_holder || null,
          iban: ibanNorm || null,
          bank_name: form.bank_name || null,
          image_url: form.image_url,
          banner_url: form.banner_url,
          instagram: form.instagram || null,
          youtube: form.youtube || null,
          linkedin: form.linkedin || null,
          facebook: form.facebook || null,
          tiktok: form.tiktok || null,
          shipping_policy: form.shipping_policy || null,
          refund_policy: form.refund_policy || null,
          message_to_buyers: form.message_to_buyers || null,
          shipping_fee: form.shipping_fee ? Number(form.shipping_fee) : 0,
          free_shipping_over: form.free_shipping_over ? Number(form.free_shipping_over) : null,
          public_phone: form.public_phone.trim(),
          phone_public: form.phone_public,
        },
      });
      setMsg({ type: "ok", text: "Shranjeno." });
      await refetch();
    } catch (err) {
      setMsg({ type: "err", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div className="mx-auto max-w-3xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  if (!data) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-serif text-3xl">Trgovine ni mogoče naložiti</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Če ste pravkar ustvarili trgovino, poskusite osvežiti podatke.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="mt-6 inline-block bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-60"
        >
          {isFetching ? "Osvežujem…" : "Osveži"}
        </button>
        <div className="mt-4">
          <Link to="/postani-prodajalec" className="text-sm text-muted-foreground hover:text-accent underline">
            Ali odpri novo trgovino
          </Link>
        </div>
      </div>
    );
  }


  return (
    <section className="mx-auto max-w-3xl px-6 py-16">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Trgovina</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">Urejanje profila</h1>
        </div>
        <Link to="/dashboard" className="border border-border px-4 py-2 text-sm hover:bg-muted">← Nadzorna plošča</Link>
      </div>

      {!isActiveMember && (
        <div className="mt-8 border border-accent bg-accent/10 p-5 text-sm">
          <p className="font-medium text-accent">Aktivirajte naročnino</p>
          <p className="mt-2 text-muted-foreground">
            Urejanje trgovine in dodajanje izdelkov je na voljo po plačilu letne najemnine (240 €). Strežnik bo zavrnil shranjevanje, dokler naročnina ni aktivna.
          </p>
          <Link to="/placilo-najemnine" className="mt-3 inline-block bg-accent text-accent-foreground px-4 py-2 text-sm">Plačaj najemnino →</Link>
        </div>
      )}

      {!payoutComplete && (
        <div className="mt-4 border border-destructive bg-destructive/10 p-5 text-sm">
          <p className="font-medium text-destructive">Obvezni podatki za izdajo predračunov</p>
          <p className="mt-2 text-muted-foreground">
            Za izdajo predračunov in računov morate izpolniti <strong>naslov, mesto, poštno številko, imetnika računa, TRR (SI56…) in telefonsko številko</strong>.
            Brez teh podatkov ne boste mogli prejemati naročil.
          </p>
        </div>
      )}

      <form className="mt-10 space-y-6" onSubmit={onSubmit}>
        <ImageUpload value={form.banner_url} onChange={(v) => upd("banner_url", v)} folder="banners" label="Banner trgovine" aspect="wide"
          alt={form.store_name ? `Banner trgovine ${form.store_name} – ročno izdelani izdelki` : "Banner trgovine"} />
        <div className="max-w-xs">
          <ImageUpload value={form.image_url} onChange={(v) => upd("image_url", v)} folder="logos" label="Logotip / profilna"
            alt={form.store_name ? `Logotip prodajalca ${form.store_name}` : "Logotip trgovine"} />
        </div>

        <div>
          <label className="text-xs eyebrow">Ime trgovine *</label>
          <input required value={form.store_name} onChange={(e) => upd("store_name", e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>

        <div>
          <label className="text-xs eyebrow">Opis</label>
          <textarea rows={5} value={form.description} onChange={(e) => upd("description", e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>

        <h2 className="font-serif text-2xl pt-6 border-t border-border">Naslov za račun *</h2>
        <p className="text-xs text-muted-foreground -mt-2">Naslov se uporablja na predračunih in računih, ki jih izdate kupcem.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={`text-xs eyebrow ${!form.address_1.trim() ? "text-destructive" : ""}`}>Ulica in hišna številka *{!form.address_1.trim() && " — manjka"}</label>
            <input required value={form.address_1} onChange={(e) => upd("address_1", e.target.value)}
              className={`mt-2 w-full bg-background border px-4 py-3 text-sm focus:outline-none ${!form.address_1.trim() ? "border-destructive bg-destructive/5" : "border-border focus:border-foreground"}`} />
          </div>
          <div>
            <label className={`text-xs eyebrow ${!form.postcode.trim() ? "text-destructive" : ""}`}>Poštna št. *{!form.postcode.trim() && " — manjka"}</label>
            <input required value={form.postcode} onChange={(e) => upd("postcode", e.target.value)}
              className={`mt-2 w-full bg-background border px-4 py-3 text-sm focus:outline-none ${!form.postcode.trim() ? "border-destructive bg-destructive/5" : "border-border focus:border-foreground"}`} />
          </div>
          <div>
            <label className={`text-xs eyebrow ${!form.city.trim() ? "text-destructive" : ""}`}>Mesto *{!form.city.trim() && " — manjka"}</label>
            <input required value={form.city} onChange={(e) => upd("city", e.target.value)}
              className={`mt-2 w-full bg-background border px-4 py-3 text-sm focus:outline-none ${!form.city.trim() ? "border-destructive bg-destructive/5" : "border-border focus:border-foreground"}`} />
          </div>
          <div className="col-span-2">
            <label className="text-xs eyebrow">Država</label>
            <input value={form.country} onChange={(e) => upd("country", e.target.value)}
              className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
          </div>
        </div>

        <h2 className="font-serif text-2xl pt-6 border-t border-border">Bančni podatki *</h2>
        <p className="text-xs text-muted-foreground -mt-2">Podatki za UPN QR na predračunih. Brez njih ne moremo izdajati predračunov v vašem imenu.</p>
        <div className="space-y-3">
          <div>
            <label className={`text-xs eyebrow ${!form.account_holder.trim() ? "text-destructive" : ""}`}>Imetnik računa (ime in priimek / naziv) *{!form.account_holder.trim() && " — manjka"}</label>
            <input required value={form.account_holder} onChange={(e) => upd("account_holder", e.target.value)}
              className={`mt-2 w-full bg-background border px-4 py-3 text-sm focus:outline-none ${!form.account_holder.trim() ? "border-destructive bg-destructive/5" : "border-border focus:border-foreground"}`} />
          </div>
          <div>
            <label className={`text-xs eyebrow ${!ibanValid ? "text-destructive" : ""}`}>TRR (IBAN, SI56…) *{!ibanValid && (form.iban ? " — neveljaven" : " — manjka")}</label>
            <input
              required
              value={form.iban}
              onChange={(e) => upd("iban", formatIban(e.target.value))}
              placeholder="SI56 1234 5678 9012 345"
              className={`mt-2 w-full bg-background border px-4 py-3 text-sm focus:outline-none ${!ibanValid ? "border-destructive bg-destructive/5" : "border-border focus:border-foreground"}`}
            />
            {form.iban && !ibanValid && (
              <p className="mt-1 text-xs text-destructive">TRR mora biti slovenski IBAN: SI56 + 15 cifer.</p>
            )}
          </div>
          <div>
            <label className="text-xs eyebrow">Banka (neobvezno)</label>
            <input value={form.bank_name} onChange={(e) => upd("bank_name", e.target.value)}
              className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
          </div>
        </div>

        <h2 className="font-serif text-2xl pt-6 border-t border-border">Kontaktni telefon *</h2>
        <p className="text-xs text-muted-foreground -mt-2">Telefonska številka je obvezna za komunikacijo z ekipo nakupovalnega središča in kupci (po vaši izbiri).</p>
        <div className="space-y-3">
          <div>
            <label className={`text-xs eyebrow ${!phoneValid ? "text-destructive" : ""}`}>Telefonska številka *{!phoneValid && (form.public_phone ? " — prekratka" : " — manjka")}</label>
            <input
              required
              type="tel"
              placeholder="npr. 040 123 456"
              value={form.public_phone}
              onChange={(e) => upd("public_phone", e.target.value)}
              className={`mt-2 w-full bg-background border px-4 py-3 text-sm focus:outline-none ${!phoneValid ? "border-destructive bg-destructive/5" : "border-border focus:border-foreground"}`}
            />
          </div>
          <div className="bg-muted/40 border border-border p-4 text-sm space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.phone_public}
                onChange={(e) => upd("phone_public", e.target.checked)}
                className="mt-1 h-4 w-4 accent-primary"
              />
              <span>
                <span className="font-medium">Želim, da je moja telefonska številka javno vidna kupcem</span>
                <span className="block text-muted-foreground text-xs mt-1">
                  Če to možnost izklopite, številko ohranimo samo za ekipo nakupovalnega središča za nujne primere in je kupci ne bodo videli na vaši strani trgovine ali pri izdelkih.
                </span>
              </span>
            </label>
          </div>
        </div>




        <h2 className="font-serif text-2xl pt-6 border-t border-border">Družbena omrežja</h2>
        {(["instagram", "youtube", "linkedin", "facebook", "tiktok"] as const).map((k) => (
          <div key={k}>
            <label className="text-xs eyebrow capitalize">{k}</label>
            <input type="url" placeholder="https://…" value={form[k]} onChange={(e) => upd(k, e.target.value)}
              className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
          </div>
        ))}

        <h2 className="font-serif text-2xl pt-6 border-t border-border">Dostava in poštnina</h2>
        <p className="text-xs text-muted-foreground -mt-2">
          Poštnino določite sami. Znesek se prikaže kupcem pri vaših izdelkih in ga lahko prenesete na predračun.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs eyebrow">Poštnina (€)</label>
            <input
              type="number" min="0" step="0.01" inputMode="decimal"
              value={form.shipping_fee}
              onChange={(e) => upd("shipping_fee", e.target.value)}
              className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
            />
            <p className="mt-1 text-xs text-muted-foreground">Vpišite 0, če poštnine ne zaračunavate.</p>
          </div>
          <div>
            <label className="text-xs eyebrow">Brezplačna poštnina nad (€)</label>
            <input
              type="number" min="0" step="0.01" inputMode="decimal"
              placeholder="npr. 50"
              value={form.free_shipping_over}
              onChange={(e) => upd("free_shipping_over", e.target.value)}
              className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
            />
            <p className="mt-1 text-xs text-muted-foreground">Pustite prazno, če te možnosti ne ponujate.</p>
          </div>
        </div>

        <h2 className="font-serif text-2xl pt-6 border-t border-border">Kartično plačilo (Stripe)</h2>
        <StripeConnectCard />

        <h2 className="font-serif text-2xl pt-6 border-t border-border">Pravila & sporočila</h2>
        <div>
          <label className="text-xs eyebrow">Sporočilo kupcem</label>
          <textarea rows={3} value={form.message_to_buyers} onChange={(e) => upd("message_to_buyers", e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>
        <div>
          <label className="text-xs eyebrow">Pravila dostave</label>
          <textarea rows={3} value={form.shipping_policy} onChange={(e) => upd("shipping_policy", e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>
        <div>
          <label className="text-xs eyebrow">Pravila vračila</label>
          <textarea rows={3} value={form.refund_policy} onChange={(e) => upd("refund_policy", e.target.value)}
            className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground" />
        </div>

        {msg && <div className={msg.type === "ok" ? "text-sm text-accent" : "text-sm text-destructive"}>{msg.text}</div>}

        <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:flex-wrap">
          <button type="submit" disabled={saving || !isActiveMember || !payoutComplete}
            title={!isActiveMember ? "Za shranjevanje je potrebna aktivna naročnina." : (!payoutComplete ? "Najprej izpolnite naslov in TRR." : undefined)}
            className="w-full sm:w-auto bg-primary text-primary-foreground px-6 py-3 text-sm text-center hover:opacity-90 disabled:opacity-60">
            {saving ? "Shranjujem…" : "Shrani spremembe"}
          </button>
          {data?.slug && (
            <a
              href={`/prodajalci/${data.slug}`}
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto border border-border px-6 py-3 text-sm text-center hover:bg-muted"
            >
              Predogled moje trgovine
            </a>
          )}
          <button
            type="button"
            disabled={deleting}
            onClick={async () => {
              if (!confirm("Ali ste prepričani, da želite izbrisati svojo trgovino? S tem se bodo izbrisali tudi vsi vaši izdelki. Tega dejanja ni mogoče razveljaviti.")) return;
              setMsg(null);
              setDeleting(true);
              try {
                await remove({});
                navigate({ to: "/dashboard" });
              } catch (err) {
                setMsg({ type: "err", text: (err as Error).message });
                setDeleting(false);
              }
            }}
            className="w-full sm:w-auto sm:ml-auto border border-destructive text-destructive px-6 py-3 text-sm text-center hover:bg-destructive hover:text-destructive-foreground disabled:opacity-60"
          >
            {deleting ? "Brišem…" : "Izbriši mojo trgovino"}
          </button>
        </div>

      </form>
    </section>
  );
}


function StripeConnectCard() {
  const statusFn = useServerFn(getMyStripeStatus);
  const startFn = useServerFn(startStripeOnboarding);
  const disableFn = useServerFn(disableStripePayments);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["my-stripe-status"],
    queryFn: () => statusFn(),
    retry: false,
  });

  async function connect() {
    setError(null);
    setBusy(true);
    try {
      const res = await startFn({ data: { origin: window.location.origin } });
      window.location.href = res.url;
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function disconnect() {
    setError(null);
    setBusy(true);
    try {
      await disableFn();
      await refetch();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const enabled = Boolean(data?.charges_enabled);

  return (
    <div className="bg-muted/40 border border-border p-5 text-sm space-y-4">
      <p className="text-muted-foreground">
        Če povežete svoj Stripe račun, bodo kupci vaše predračune lahko plačali s kartico, denar pa gre neposredno vam.
        Dokler plačevanja ne uredite, imajo kupci pri vaši trgovini na voljo samo plačilo po UPN nalogu.
      </p>

      {isLoading ? (
        <p className="text-muted-foreground">Preverjam stanje…</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 text-xs ${
              enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            }`}
          >
            {enabled ? "Kartično plačilo je vklopljeno" : "Kartično plačilo ni urejeno"}
          </span>
          {!enabled && (
            <button type="button" onClick={connect} disabled={busy}
              className="bg-accent text-accent-foreground px-4 py-2 text-sm disabled:opacity-50">
              {busy ? "Odpiram…" : data?.connected ? "Dokončaj nastavitev →" : "Poveži Stripe →"}
            </button>
          )}
          {enabled && (
            <>
              <button type="button" onClick={connect} disabled={busy}
                className="border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
                Uredi podatke
              </button>
              <button type="button" onClick={disconnect} disabled={busy}
                className="border border-destructive/40 text-destructive px-4 py-2 text-sm hover:bg-destructive/5 disabled:opacity-50">
                Izklopi kartično plačilo
              </button>
            </>
          )}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
